import React, { useState, useEffect } from 'react';
import {
  TrendingUp, ChevronRight, AlertCircle, Clock, UserPlus, ExternalLink,
  AlertTriangle, RefreshCw, Target, Sun, Snowflake
} from 'lucide-react';
import { reportsApi } from '../../services/api';
import Loading from '../Loading';

const safeCurrency = (amount) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

// Icons the backend can request for action items
const iconMap = { TrendingUp, AlertCircle, Clock, UserPlus, Target };

const HealthScorecard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAction, setExpandedAction] = useState(null);

  useEffect(() => {
    loadScorecard();
  }, []);

  const loadScorecard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportsApi.getHealthScorecard();
      setData(response.data);
    } catch (err) {
      console.error('Error loading scorecard:', err);
      setError('Unable to load action plan');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
        <p className="text-gray-600">{error}</p>
        <button onClick={loadScorecard} className="btn btn-primary mt-4">
          Try Again
        </button>
      </div>
    );
  }

  if (!data) return null;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-red-500 bg-red-50';
      case 'medium': return 'border-l-amber-500 bg-amber-50';
      case 'low': return 'border-l-blue-500 bg-blue-50';
      default: return 'border-l-gray-500 bg-gray-50';
    }
  };

  return (
    <div className="space-y-4">
      {/* Action Items */}
      {data.recommendedActions && data.recommendedActions.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="p-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Target size={18} className="text-primary-600" />
              <h3 className="font-semibold text-gray-900">Top Actions This Week</h3>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {data.recommendedActions.map((action, index) => {
              const Icon = iconMap[action.icon] || AlertCircle;
              const isExpanded = expandedAction === action.id;

              return (
                <div key={action.id} className={`border-l-4 ${getPriorityColor(action.priority)}`}>
                  <button
                    onClick={() => setExpandedAction(isExpanded ? null : action.id)}
                    className="w-full p-3 text-left hover:bg-white/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm font-bold text-gray-700">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className="text-gray-500 flex-shrink-0" />
                          <p className="font-medium text-gray-900 text-sm">{action.title}</p>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5">{action.description}</p>
                        {action.potentialValue > 0 && (
                          <p className="text-xs font-medium text-green-600 mt-1">
                            Potential: {safeCurrency(action.potentialValue)}
                          </p>
                        )}
                      </div>
                      <ChevronRight
                        size={18}
                        className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </div>
                  </button>

                  {isExpanded && action.targets && action.targets.length > 0 && (
                    <div className="px-3 pb-3 pt-0">
                      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                        {action.targets.map((target) => (
                          <div key={target.id} className="px-3 py-2 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{target.name}</p>
                              <p className="text-xs text-gray-500">{target.detail}</p>
                            </div>
                            {target.value > 0 && (
                              <span className="text-sm font-medium text-gray-700">
                                {safeCurrency(target.value)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {action.actionUrl && (
                        <a
                          href={action.actionUrl}
                          className="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Take Action <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Forecast */}
      {data.forecast && (
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={18} className="text-primary-600" />
            <h3 className="font-semibold text-gray-900">Forecast</h3>
            {data.forecast.projectionConfidence && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                data.forecast.projectionConfidence === 'high' ? 'bg-green-100 text-green-700' :
                data.forecast.projectionConfidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {data.forecast.projectionConfidence} confidence
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-gray-500">Projected Annual</p>
              <p className="text-xl font-bold text-gray-900">
                {safeCurrency(data.forecast.projectedAnnualRevenue)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Next 30 Days</p>
              <p className="text-xl font-bold text-primary-600">
                {safeCurrency(data.forecast.next30DaysScheduled)}
              </p>
              <p className="text-xs text-gray-500">scheduled</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Next 60 Days</p>
              <p className="text-lg font-semibold text-gray-700">
                {safeCurrency(data.forecast.next60DaysScheduled)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Next 90 Days</p>
              <p className="text-lg font-semibold text-gray-700">
                {safeCurrency(data.forecast.next90DaysScheduled)}
              </p>
            </div>
          </div>

          {data.forecast.seasonalNote && (
            <div className={`flex items-center gap-2 p-3 rounded-lg ${
              data.forecast.isBusySeason ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'
            }`}>
              {data.forecast.isBusySeason ? <Sun size={16} /> : <Snowflake size={16} />}
              <p className="text-sm">{data.forecast.seasonalNote}</p>
            </div>
          )}
        </div>
      )}

      {/* Tax Reminders */}
      {data.upcomingTaxDates && data.upcomingTaxDates.length > 0 && (
        <div className="card p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-purple-600" />
            <h3 className="font-semibold text-purple-900">Tax Reminders</h3>
          </div>
          <div className="space-y-2">
            {data.upcomingTaxDates.map((tax, i) => (
              <div
                key={i}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  tax.isUrgent ? 'bg-red-100 border border-red-200' : 'bg-white'
                }`}
              >
                <div>
                  <p className={`font-medium ${tax.isUrgent ? 'text-red-900' : 'text-gray-900'}`}>
                    {tax.label}
                  </p>
                  <p className={`text-xs ${tax.isUrgent ? 'text-red-700' : 'text-gray-500'}`}>
                    Due {new Date(tax.dueDate).toLocaleDateString()}
                    {tax.isUrgent && ` (${tax.daysUntilDue} days!)`}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${tax.isUrgent ? 'text-red-900' : 'text-gray-900'}`}>
                    ~{safeCurrency(tax.estimatedAmount)}
                  </p>
                  <p className="text-xs text-gray-500">estimated</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center">
        <button
          onClick={loadScorecard}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>
    </div>
  );
};

export default HealthScorecard;